import React, { useEffect, useState } from 'react';
import { translate } from '../../services/translate';
import Container from '../../components/Container';
import Header from '../../components/Header';
import { NavigationProp, useNavigation, RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { StreamServersParamList } from '../../typings/Params';
import Card from '../../components/Card';
import { Tabs } from '../../components/Tabs';
import { streamServersService } from '../../services/central-api/stream-servers';
import { useQuery } from '@tanstack/react-query';
import ServerFarmDetailServerPools from './ServerFarmDetailServerPools';
import ServerFarmDetailTimelineProviders from './ServerFarmDetailTimelineProviders';
import ServerFarmDetailVideoStorageProviders from './ServerFarmDetailVideoStorageProviders';
import ServerFarmDetailServers from './ServerFarmDetailServers';

export default function ServerFarmDetail() {
    const navigation = useNavigation<NavigationProp<StreamServersParamList>>();
    const route = useRoute<RouteProp<StreamServersParamList, 'ServerFarmDetail'>>();
    const [serverPoolId, setServerPoolId] = useState<string | null>(null);
    const [videoStorageProviderId, setVideoStorageProviderId] = useState<number | null>(null);
    const [timelineProviderId, setTimelineProviderId] = useState<number | null>(null);
    const [selectedTab, setSelectedTab] = useState<'serverPools' | 'timelineProviders' | 'videoStorageProviders' | 'servers' | null>('serverPools');

    useEffect(() => {
        if (selectedTab === null) {
            setSelectedTab('servers');
        }
    }, [selectedTab]);

    const isFocused = useIsFocused();
    const { data: serverFarm, refetch } = useQuery({
        queryKey: ['getServerFarmDetail', route.params.id],
        queryFn: () => streamServersService.getServerFarmById(route.params.id),
        enabled: isFocused && Boolean(route.params.id),
    });

    if (!serverFarm) {
        return <Container></Container>;
    }

    return <Container>
        <Header
            title={`${translate('serverFarmDetail')}: ${route.params.id} - ${serverFarm.companyName}`}
            goBackAction={() => {
                if (navigation.canGoBack()) {
                    return navigation.goBack();
                }
                navigation.navigate('ServerFarms');
            }}
        />
        <Card>
            <Tabs
                tabs={[{
                    key: 'serverPools',
                    label: translate('serverPools')
                }, {
                    key: 'timelineProviders',
                    label: translate('timelineProviders')
                }, {
                    key: 'videoStorageProviders',
                    label: translate('videoStorageProviders'),
                }, {
                    key: 'servers',
                    label: translate('servers'),
                }]}
                selectedTab={selectedTab ?? 'serverPools'}
            >
                {({ selectedTab }) => {
                    if (selectedTab == 'serverPools') {
                        return <ServerFarmDetailServerPools
                            refetch={refetch}
                            serverFarm={serverFarm}
                            setSelectedTab={setSelectedTab}
                            setServerPoolId={setServerPoolId}
                            setTimelineProviderId={setTimelineProviderId}
                            setVideoStorageProviderId={setVideoStorageProviderId}
                        />;
                    } else if (selectedTab == 'timelineProviders') {
                        return <ServerFarmDetailTimelineProviders
                            refetch={refetch}
                            serverFarm={serverFarm}
                            setSelectedTab={setSelectedTab}
                            setServerPoolId={setServerPoolId}
                            setTimelineProviderId={setTimelineProviderId}
                            setVideoStorageProviderId={setVideoStorageProviderId}
                        />;
                    } else if (selectedTab == 'videoStorageProviders') {
                        return <ServerFarmDetailVideoStorageProviders
                            refetch={refetch}
                            serverFarm={serverFarm}
                            setSelectedTab={setSelectedTab}
                            setServerPoolId={setServerPoolId}
                            setTimelineProviderId={setTimelineProviderId}
                            setVideoStorageProviderId={setVideoStorageProviderId}
                        />;
                    } else if (selectedTab == 'servers') {
                        return <ServerFarmDetailServers
                            refetch={refetch}
                            serverFarm={serverFarm}
                            setServerPoolId={setServerPoolId}
                            setTimelineProviderId={setTimelineProviderId}
                            setVideoStorageProviderId={setVideoStorageProviderId}
                            serverPoolId={serverPoolId}
                            timelineProviderId={timelineProviderId}
                            videoStorageProviderId={videoStorageProviderId}
                        />;
                    }
                }}
            </Tabs>
        </Card>
    </Container>;
}
